import type { IErrorHandlingRequestState } from 'features/errorHandling/errorHandlingSlice';
import { RequestStatus } from 'features/request/requestSlice';
import { useRequest } from 'features/request/useRequest';
import { useResetStoreData } from 'features/request/useResetStoreData';
import type { CatalogUerBaseCostDetailedData } from 'gql/graphql';
import { ExportCSVReport } from 'utils/createCSVReportFile';

import { useCallback, useEffect, useMemo, useState } from 'react';
import type { UseFormGetValues, UseFormSetValue } from 'react-hook-form';

import { catalogParseUerBaseCostBasisGql } from '../createOrEditUerQueries';

interface IParseUerCatalog {
    reset: (data: Record<string, any>) => void;
    getValues: UseFormGetValues<Record<string, any>>;
    setValue: UseFormSetValue<Record<string, any>>;
    rowData: CatalogUerBaseCostDetailedData | null | undefined;
    isEdit: boolean;
    catalogVersion?: string;
    catalogName: string;
}

const WRONG_PARSE_PARAMS_MSG =
    'В загружаемом файле сметы-основания параметры не совпадают с параметрами расценки. Пожалуйста, внесите корректировки и повторно загрузите файл.';
const WRONG_PARSE_PARAMS_CODE_MSG = 'Неверный шифр расценки';
const WRONG_PARSE_PARAMS_UNIT_MSG = 'Неверная ед.изм.';
const WRONG_PARSE_PARAMS_GROUP_MSG = 'Неверный подраздел';
const WRONG_PARSE_PARAMS_SECTION_NAME_MSG = 'Неверный раздел';

export const useParseUerCatalog = (props: IParseUerCatalog) => {
    const { reset, getValues, setValue, rowData, isEdit, catalogVersion, catalogName } = props;

    const [uerDataParseRequestSuccess, setUerDataParseRequestSuccess] = useState<boolean>(false);
    const [wrongParseUerParameters, setWrongParseUerParameters] = useState<boolean>(false);
    const [wrongParseUerParametersData, setWrongParseUerParametersData] = useState<
        IErrorHandlingRequestState | undefined
    >(undefined);

    const wrongParseUerParametersCreateReport = useCallback(() => {
        ExportCSVReport(wrongParseUerParametersData?.validation);
    }, [wrongParseUerParametersData?.validation]);

    const [parsedUerData, parsedUerStatus, parsedUerError, parsedUerRequest] = useRequest({
        requestId: 'catalogParseUerBaseCostBasis',
        query: catalogParseUerBaseCostBasisGql,
        withEffect: false,
    });

    const { sectionName: rowSectionName, groupName: rowGroupName, code: rowCode, unit: rowUnit } = rowData || {};

    const {
        sectionName: parseSectionName,
        groupName: parseGroupName,
        code: parseCode,
        unit: parseUnit,
    } = parsedUerData?.catalogParseUerBaseCostBasis?.data?.general || {};

    const parsedVersion = parsedUerData?.catalogParseUerBaseCostBasis?.data?.version;

    const updateDate = useMemo(() => {
        if (!parsedUerData?.catalogParseUerBaseCostBasis?.data) {
            return {};
        }

        const { chapter, ...rest } = parsedUerData?.catalogParseUerBaseCostBasis.data?.general || {};
        const { workTypeItems, uerNacl, uerPlan, catFile, workID } = parsedUerData.catalogParseUerBaseCostBasis.data;

        return {
            ...rest,
            ...parsedUerData.catalogParseUerBaseCostBasis.data.baseCost,
            workTypeItems,
            uerNacl,
            uerPlan,
            catFile,
            workID,
        };
    }, [parsedUerData?.catalogParseUerBaseCostBasis?.data]);

    const DOWNLOADABLE_ESTIMATE_MSG = `Загружаемая смета-основание рассчитана по методике (${parsedVersion}), не соответствующей справочнику расценок, в рамках которого загружен Договор (${catalogName})`;

    useEffect(() => {
        if (parsedUerStatus === RequestStatus.SUCCESS) {
            setUerDataParseRequestSuccess(true);
            if (parsedUerData?.catalogParseUerBaseCostBasis?.result) {
                const {
                    justification,
                    uerClassifier,
                    ratioGroupIds,
                    ratioGroups,
                    ratioIds,
                    ratioItems,
                    groupsDismantling,
                    workKind,
                    workCode,
                    workUnit,
                    workInSum,
                    workRatio,
                    ratios,
                } = getValues();

                if (!isEdit && parsedVersion !== catalogVersion) {
                    setWrongParseUerParameters(true);
                    setWrongParseUerParametersData({
                        result: false,
                        buttonText: 'Понятно',
                        message: WRONG_PARSE_PARAMS_MSG,
                        validation: [
                            ...(parsedVersion !== catalogVersion
                                ? [
                                      {
                                          message: DOWNLOADABLE_ESTIMATE_MSG,
                                      },
                                  ]
                                : []),
                        ],
                    });
                }

                if (
                    isEdit &&
                    (parseSectionName !== rowSectionName ||
                        parseGroupName !== rowGroupName ||
                        parsedVersion !== catalogVersion ||
                        parseCode !== rowCode ||
                        parseUnit !== rowUnit)
                ) {
                    setWrongParseUerParameters(true);

                    setWrongParseUerParametersData({
                        result: false,
                        buttonText: 'Понятно',
                        message: WRONG_PARSE_PARAMS_MSG,
                        validation: [
                            ...(catalogVersion !== parsedVersion
                                ? [
                                      {
                                          message: DOWNLOADABLE_ESTIMATE_MSG,
                                      },
                                  ]
                                : []),
                            ...(rowCode !== parseCode
                                ? [
                                      {
                                          message: WRONG_PARSE_PARAMS_CODE_MSG,
                                      },
                                  ]
                                : []),
                            ...(rowUnit !== parseUnit
                                ? [
                                      {
                                          message: WRONG_PARSE_PARAMS_UNIT_MSG,
                                      },
                                  ]
                                : []),
                            ...(rowGroupName !== parseGroupName
                                ? [
                                      {
                                          message: WRONG_PARSE_PARAMS_GROUP_MSG,
                                      },
                                  ]
                                : []),
                            ...(rowSectionName !== parseSectionName
                                ? [
                                      {
                                          message: WRONG_PARSE_PARAMS_SECTION_NAME_MSG,
                                      },
                                  ]
                                : []),
                        ],
                    });
                } else {
                    reset({
                        ...updateDate,
                        justification,
                        uerClassifier,
                        ratioGroupIds,
                        ratioGroups,
                        groupsDismantling,
                        ratioIds,
                        ratioItems,
                        workKind,
                        workCode,
                        workUnit,
                        workInSum,
                        workRatio,
                        ratios,
                    });
                }
            }
        }
    }, [
        DOWNLOADABLE_ESTIMATE_MSG,
        catalogName,
        catalogVersion,
        getValues,
        isEdit,
        parseCode,
        parseGroupName,
        parseSectionName,
        parseUnit,
        parsedUerData?.catalogParseUerBaseCostBasis.data,
        parsedUerData?.catalogParseUerBaseCostBasis?.result,
        parsedUerStatus,
        parsedVersion,
        reset,
        rowCode,
        rowGroupName,
        rowSectionName,
        rowUnit,
        setValue,
        updateDate,
    ]);

    const [resetParsedDataHandler] = useResetStoreData({ requestId: 'catalogParseUerBaseCostBasis' });

    const onCloseParseErrorModal = useCallback(() => {
        setUerDataParseRequestSuccess(false);
        resetParsedDataHandler(null);
        setWrongParseUerParameters(false);
        setWrongParseUerParametersData(undefined);
    }, [resetParsedDataHandler]);

    return {
        uerDataParseRequestSuccess,
        wrongParseUerParametersData,
        setUerDataParseRequestSuccess,
        parsedUerData: parsedUerData?.catalogParseUerBaseCostBasis,
        parsedUerStatus,
        parsedUerRequest,
        resetParsedDataHandler,
        onCloseParseErrorModal,
        parsedUerError,
        wrongParseUerParametersCreateReport,
        wrongParseUerParameters,
    };
};
